import styled from '@emotion/styled'
import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { HomeFilters } from '@sales-app/context/workspace/workspace.type'
import React, { ChangeEvent, FC, useState } from 'react'

import { Dropdown } from '@/components'

type Props = {
  currentFilters: HomeFilters
}

export const PriceRangeSelection: FC<Props> = ({ currentFilters }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [, WorkspaceDispatch] = useWorkspaceContext()
  const { minPrice, maxPrice } = currentFilters
  const highestMaxPrice = 20000
  const highestMinPrice = 10000

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target

    if (name === 'minPrice') {
      WorkspaceDispatch(WorkspaceAction.updateFilters({ minPrice: value }))
    } else {
      WorkspaceDispatch(WorkspaceAction.updateFilters({ maxPrice: value }))
    }

    if (name === 'minPrice' && maxPrice && parseInt(value) > parseInt(maxPrice)) {
      const updatedMaxPrice = parseInt(value) + 10
      WorkspaceDispatch(
        WorkspaceAction.updateFilters({
          maxPrice: isExceedMaxPrice(updatedMaxPrice) ? highestMaxPrice.toString() : '',
        }),
      )
    }
  }

  const invalidChars = ['-', '+', 'e', '.']
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (invalidChars.includes(e.key)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  function isExceedMaxPrice(value: number) {
    return value > highestMaxPrice
  }

  function handleBlur(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    if (parseInt(value) < 10) {
      if (name === 'minPrice') {
        WorkspaceDispatch(WorkspaceAction.updateFilters({ minPrice: '10' }))
      } else {
        WorkspaceDispatch(WorkspaceAction.updateFilters({ maxPrice: '10' }))
      }
    }
    if (name === 'maxPrice' && parseInt(value) > highestMaxPrice) {
      WorkspaceDispatch(WorkspaceAction.updateFilters({ maxPrice: '' + highestMaxPrice }))
    }

    if (name === 'minPrice' && parseInt(value) > highestMinPrice) {
      WorkspaceDispatch(WorkspaceAction.updateFilters({ minPrice: '' + highestMinPrice }))
    }

    if (name === 'maxPrice' && minPrice && parseInt(value) < parseInt(minPrice)) {
      const newMinPrice = parseInt(value) - 10
      WorkspaceDispatch(WorkspaceAction.updateFilters({ minPrice: newMinPrice > 10 ? newMinPrice.toString() : '10' }))
    }
  }

  const dropdownHeader = minPrice || maxPrice ? `${minPrice} ${maxPrice ? `- ${maxPrice}` : ''}` : '---'

  return (
    <Dropdown
      className="my-2 md:my-0"
      expanded={isOpen}
      fullScreenMobile={true}
      onHideDropdown={() => {}}
      renderToggle={() => {
        return <span className="underline">{dropdownHeader}</span>
      }}
      setExpanded={setIsOpen}
    >
      <div className="flex justify-between px-4 pb-6">
        <div className="w-full">
          <div>
            <label className="text-grey-800 mb-2 font-medium leading-120" htmlFor="min-price">
              Min Price
            </label>
          </div>
          <InputMonthlyStyled className="text-grey-500">
            <InputStyled
              className="mt-2 flex w-full rounded-lg border border-grey-400 py-4 pl-6 font-medium text-base leading-120 shadow-none outline-none border-secondary"
              id="min-price"
              min={0}
              name="minPrice"
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              type="number"
              value={minPrice ?? ''}
            />
          </InputMonthlyStyled>
        </div>
        <div className="mx-4 pt-12 text-grey-600">—</div>
        <div className="w-full">
          <div>
            <label className="text-grey-800 mb-2 font-medium text-label-sm leading-120" htmlFor="max-price">
              Max Price
            </label>
          </div>
          <InputMonthlyStyled className="text-grey-500">
            <InputStyled
              className="md:w-full"
              id="max-price"
              min={0}
              name="maxPrice"
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              type="number"
              value={maxPrice ?? ''}
            />
          </InputMonthlyStyled>
        </div>
      </div>
    </Dropdown>
  )
}

const InputStyled = styled.input`
  border-color: #e7e6e4;
  display: flex;
  margin-top: 0.5rem;
  padding-left: 1.5rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  width: 100%;
  font-weight: 500;
  font-size: 1rem;
  line-height: 120%;
  border-width: 1px;
  border-color: #a7a6a4;
  border-radius: 0.5rem;
  outline: none;
  box-shadow: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' height='16px' width='85px'><text x='2' y='13' fill='gray' font-size='14' font-family='arial'>$</text></svg>");
  background-repeat: no-repeat;
  background-position: 10px 53%;

  @media (min-width: 768px) {
    padding-right: 3rem;
    width: 100%;
    min-width: 9rem;
  }
`

const InputMonthlyStyled = styled.div`
  width: 100%;
  display: inline-block;
  position: relative;

  &::after {
    content: '/mo';
    color: 'lightgrey';
    position: absolute;
    top: 40%;
    right: 0.8em;
    transition: all 0.05s ease-in-out;
  }
`
